import React, { useState } from 'react';
import { Github, Eye, Lock, Book, Filter, Search, X, Clock, Users, CheckCircle, AlertCircle } from 'lucide-react';
import { projects } from '../../mock/data';
import { useModalAccessibility } from '../../hooks/useModalAccessibility';
import './Projects.css';

// `duration` e `team` nasceram como string simples e alguns projetos já usam
// a forma bilíngue { pt, en }. Renderizar o objeto cru quebraria o React,
// então normaliza os dois formatos.
const localized = (value, language) =>
  value && typeof value === 'object' ? value[language] : value;

const Projects = ({ language }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  // Índice do screenshot em destaque na galeria do modal (projetos que tiverem).
  const [activeShot, setActiveShot] = useState(0);

  const projectsContent = {
    pt: {
      title: 'Meus Projetos',
      subtitle: 'Uma coleção cuidadosamente selecionada dos meus principais projetos de desenvolvimento',
      filters: {
        all: 'Todos',
        public: 'Públicos',
        private: 'Privados'
      },
      search: 'Buscar projetos...',
      viewDemo: 'Ver Demo',
      viewCode: 'Ver Código',
      private: 'Privado',
      technologies: 'Tecnologias:',
      noResults: 'Nenhum projeto encontrado para sua busca.',
      stats: {
        total: 'Total de Projetos',
        public: 'Projetos Públicos',
        private: 'Projetos Privados'
      },
      modal: {
        close: 'Fechar',
        features: 'Principais Funcionalidades',
        challenges: 'Desafios Superados',
        duration: 'Duração',
        team: 'Equipe',
        technologies: 'Tecnologias Utilizadas',
        viewDetails: 'Ver Detalhes',
        screenshots: 'Capturas de tela'
      }
    },
    en: {
      title: 'My Projects',
      subtitle: 'A carefully curated collection of my main development projects',
      filters: {
        all: 'All',
        public: 'Public',
        private: 'Private'
      },
      search: 'Search projects...',
      viewDemo: 'View Demo',
      viewCode: 'View Code',
      private: 'Private',
      technologies: 'Technologies:',
      noResults: 'No projects found for your search.',
      stats: {
        total: 'Total Projects',
        public: 'Public Projects', 
        private: 'Private Projects'
      },
      modal: {
        close: 'Close',
        features: 'Key Features',
        challenges: 'Challenges Overcome',
        duration: 'Duration',
        team: 'Team',
        technologies: 'Technologies Used',
        viewDetails: 'View Details',
        screenshots: 'Screenshots'
      }
    }
  };

  const content = projectsContent[language];

  const filteredProjects = projects.filter(project => {
    const matchesFilter = filter === 'all' || project.status === filter;
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description[language].toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.technologies.some(tech => 
                           tech.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: projects.length,
    public: projects.filter(p => p.status === 'public').length,
    private: projects.filter(p => p.status === 'private').length
  };

  const openModal = (project) => {
    setSelectedProject(project);
    setActiveShot(0);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'unset';
  };

  const modalRef = useModalAccessibility(Boolean(selectedProject), closeModal);

  // Cards são <div>: replicam no teclado o que o clique já fazia.
  const handleCardKeyDown = (event, project) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openModal(project);
    }
  };

  return (
    <section id="projects" className="projects">
      <div className="container">
        <div className="projects-header">
          <h2 className="section-title">{content.title}</h2>
          <p className="section-subtitle">{content.subtitle}</p>
        </div>

        <div className="projects-stats">
          <div className="stat-card">
            <div className="stat-icon">
              <Book size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-number">{stats.total}</div>
              <div className="stat-label">{content.stats.total}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Github size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-number">{stats.public}</div>
              <div className="stat-label">{content.stats.public}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Lock size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-number">{stats.private}</div>
              <div className="stat-label">{content.stats.private}</div>
            </div>
          </div>
        </div>

        <div className="projects-controls">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder={content.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-container">
            <Filter size={20} className="filter-icon" />
            <div className="filter-buttons">
              {Object.entries(content.filters).map(([key, label]) => (
                <button
                  key={key}
                  className={`filter-btn ${filter === key ? 'active' : ''}`}
                  onClick={() => setFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="projects-grid">
          {filteredProjects.length > 0 ? (
            filteredProjects.map((project) => (
              <div
                key={project.id}
                className="project-card"
                role="button"
                tabIndex={0}
                aria-label={`${project.title} — ${content.modal.viewDetails}`}
                onClick={() => openModal(project)}
                onKeyDown={(e) => handleCardKeyDown(e, project)}
              >
                <div className="project-image">
                  <div className={`project-header bg-gradient-to-br ${project.gradient || 'from-gray-500 to-gray-700'}`}>
                    <div className="project-icon">
                      <span className="text-4xl">{project.icon || '📁'}</span>
                    </div>
                    {/* Sem imagem, o card fica só com o gradiente + emoji. */}
                    {project.image && (
                      <img
                        src={project.image}
                        alt={project.title}
                        className="project-bg-image"
                        onError={(e) => {
                          e.target.style.opacity = '0';
                        }}
                        onLoad={(e) => {
                          e.target.style.opacity = '0.3';
                        }}
                      />
                    )}
                  </div>
                  <div className="project-overlay">
                    <div className="project-actions">
                      {project.links.demo && (
                        <a
                          href={project.links.demo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn demo-btn"
                          title={content.viewDemo}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Eye size={16} />
                          <span>{content.viewDemo}</span>
                        </a>
                      )}
                      
                      {project.links.github ? (
                        <a
                          href={project.links.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn github-btn"
                          title={content.viewCode}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Github size={16} />
                          <span>{content.viewCode}</span>
                        </a>
                      ) : (
                        <div className="action-btn private-btn" title={content.private}>
                          <Lock size={16} />
                          <span>{content.private}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  

                  
                  <div className="project-status">
                    <span className={`status-badge ${project.status}`}>
                      {project.status === 'public' ? (
                        <Github size={12} />
                      ) : (
                        <Lock size={12} />
                      )}
                    </span>
                  </div>
                </div>

                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">
                    {project.description[language]}
                  </p>
                  
                  <div className="project-tech">
                    <span className="tech-label">{content.technologies}</span>
                    <div className="tech-tags">
                      {project.technologies.map((tech, index) => (
                        <span key={index} className="tech-tag">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">
              <div className="no-results-icon">
                <Search size={48} />
              </div>
              <h3 className="no-results-title">
                {content.noResults}
              </h3>
              <p className="no-results-description">
                {language === 'pt' 
                  ? 'Tente ajustar os filtros ou termo de busca.'
                  : 'Try adjusting the filters or search term.'
                }
              </p>
              <button 
                className="btn-secondary"
                onClick={() => {
                  setFilter('all');
                  setSearchTerm('');
                }}
              >
                {language === 'pt' ? 'Limpar Filtros' : 'Clear Filters'}
              </button>
            </div>
          )}
        </div>

        <div className="projects-cta">
          <div className="cta-content">
            <h3 className="cta-title">
              {language === 'pt' 
                ? 'Interessado em colaborar?' 
                : 'Interested in collaborating?'
              }
            </h3>
            <p className="cta-description">
              {language === 'pt'
                ? 'Estou sempre aberto a novos projetos e oportunidades interessantes.'
                : 'I\'m always open to new projects and interesting opportunities.'
              }
            </p>
            <div className="cta-buttons">
              <a
                href="https://github.com/pazzutti"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                <Github size={16} />
                {language === 'pt' ? 'Ver GitHub' : 'View GitHub'}
              </a>
              <button 
                className="btn-secondary"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {language === 'pt' ? 'Entre em Contato' : 'Get in Touch'}
              </button>
            </div>
          </div>
        </div>

        {/* Project Modal */}
        {selectedProject && (
          <div className="project-modal" onClick={closeModal}>
            <div
              className="modal-content"
              ref={modalRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="project-modal-title"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="modal-close"
                onClick={closeModal}
                aria-label={content.modal.close}
              >
                <X size={20} />
              </button>

              <div className="modal-header">
                <div className="modal-project-icon">
                  <span>{selectedProject.icon}</span>
                </div>
                <div className="modal-title-section">
                  <h3 className="modal-title" id="project-modal-title">{selectedProject.title}</h3>
                  <div className="modal-meta">
                    <div className="modal-meta-item">
                      <Clock size={16} />
                      <span>{content.modal.duration}: {localized(selectedProject.duration, language) || '2-3 meses'}</span>
                    </div>
                    <div className="modal-meta-item">
                      <Users size={16} />
                      <span>{content.modal.team}: {localized(selectedProject.team, language) || 'Individual'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Galeria opcional: com `screenshots`, o destaque é o print
                  selecionado; sem ele, cai no comportamento antigo (image +
                  gradiente). Sem nenhum dos dois, omite o bloco inteiro —
                  .modal-gradient usa mix-blend-mode e deixaria um vazio de 300px. */}
              {(() => {
                const shots = selectedProject.screenshots;
                const featured = shots?.length ? shots[Math.min(activeShot, shots.length - 1)] : null;
                const featuredImage = featured?.image || selectedProject.image;

                if (!featuredImage) return null;

                return (
                  <>
                    <div className={`modal-image ${featured ? 'modal-image-shot' : ''}`}>
                      <img
                        src={featuredImage}
                        alt={featured ? featured.caption[language] : selectedProject.title}
                      />
                      {/* O gradiente tinge a arte genérica; num screenshot ele
                          só atrapalharia a leitura. */}
                      {!featured && (
                        <div className={`modal-gradient bg-gradient-to-br ${selectedProject.gradient}`}></div>
                      )}
                    </div>

                    {featured && (
                      <div className="modal-gallery">
                        <p className="modal-gallery-caption">{featured.caption[language]}</p>

                        {shots.length > 1 && (
                          <div className="modal-thumbs" role="tablist" aria-label={content.modal.screenshots}>
                            {shots.map((shot, index) => {
                              const isActive = index === Math.min(activeShot, shots.length - 1);
                              return (
                                <button
                                  key={shot.image}
                                  type="button"
                                  role="tab"
                                  aria-selected={isActive}
                                  aria-label={shot.caption[language]}
                                  title={shot.caption[language]}
                                  className={`modal-thumb ${isActive ? 'active' : ''}`}
                                  onClick={() => setActiveShot(index)}
                                >
                                  <img src={shot.image} alt="" loading="lazy" />
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="modal-body">
                <div className="modal-description">
                  <p>{selectedProject.fullDescription?.[language] || selectedProject.description[language]}</p>
                </div>

                {selectedProject.features && (
                  <div className="modal-section">
                    <h4 className="modal-section-title">
                      <CheckCircle size={20} />
                      {content.modal.features}
                    </h4>
                    <ul className="modal-list">
                      {selectedProject.features[language].map((feature, index) => (
                        <li key={index} className="modal-list-item">
                          <CheckCircle size={16} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedProject.challenges && (
                  <div className="modal-section">
                    <h4 className="modal-section-title">
                      <AlertCircle size={20} />
                      {content.modal.challenges}
                    </h4>
                    <ul className="modal-list">
                      {selectedProject.challenges[language].map((challenge, index) => (
                        <li key={index} className="modal-list-item">
                          <AlertCircle size={16} />
                          <span>{challenge}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="modal-section">
                  <h4 className="modal-section-title">
                    <Book size={20} />
                    {content.modal.technologies}
                  </h4>
                  <div className="modal-tech-tags">
                    {selectedProject.technologies.map((tech, index) => (
                      <span key={index} className="modal-tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="modal-actions">
                  {selectedProject.links.demo && (
                    <a
                      href={selectedProject.links.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Eye size={16} />
                      {content.viewDemo}
                    </a>
                  )}
                  
                  {selectedProject.links.github ? (
                    <a
                      href={selectedProject.links.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Github size={16} />
                      {content.viewCode}
                    </a>
                  ) : (
                    <div className="btn-disabled">
                      <Lock size={16} />
                      {content.private}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;